import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { EventBusService } from '../../services/event-bus.service';
import { OpenSearchDrawerService } from '../../services/open-search-drawer.service';
import { SearchType } from '../../enums/search-type';

@Component({
  selector: 'app-global-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
  ],
  templateUrl: './global-sidebar.component.html',
  styleUrl: './global-sidebar.component.scss',
})
export class GlobalSidebarComponent {
  private readonly router = inject(Router);
  private readonly eventBus = inject(EventBusService);
  private readonly sourcesDrawer = inject(OpenSearchDrawerService);
  private readonly destroyRef = inject(DestroyRef);

  readonly searchSelected = output<SearchType>();
  readonly searchClosed = output<void>();
  readonly activeSearchType = input<SearchType>(SearchType.DEFAULT);
  readonly showLessonsOptions = input(false);
  readonly expanded = signal(true);
  readonly url = signal(this.router.url);
  readonly isLessonForm = signal(sessionStorage.getItem('formflow') === 'true');

  readonly isOpenSearch = computed(
    () =>
      this.url().includes('isOpenSideBarSearch=true') &&
      this.activeSearchType() !== SearchType.RECENT,
  );
  readonly isRecentSearch = computed(
    () =>
      this.url().includes('isOpenSideBarSearch=true') &&
      this.activeSearchType() === SearchType.RECENT,
  );
  readonly isLessons = computed(
    () => /^\/(lessons-learning|lessons-view)(\/|$)/.test(this.url()) && !this.isLessonForm(),
  );
  readonly isHome = computed(() => /^\/(home|projects|knowledge)(\/|$)/.test(this.url()));

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => this.url.set(event.urlAfterRedirects));
  }

  toggle(): void {
    this.expanded.update(value => !value);
  }

  goHome(): void {
    this.isLessonForm.set(false);
    this.leaveLessonIfNeeded();
    this.searchClosed.emit();
    void this.router.navigate(['/home']);
  }

  openAssistant(): void {
    this.isLessonForm.set(false);
    this.leaveLessonIfNeeded();
    this.searchSelected.emit(SearchType.DEFAULT);
  }

  openRecent(): void {
    this.isLessonForm.set(false);
    this.leaveLessonIfNeeded();
    this.searchSelected.emit(SearchType.RECENT);
  }

  openLessons(): void {
    this.sourcesDrawer.close();
    this.isLessonForm.set(false);
    sessionStorage.removeItem('selectedSet');
    sessionStorage.setItem('stflow', 'true');
    sessionStorage.setItem('formflow', 'false');
    sessionStorage.setItem('lessonFlow', 'false');
    sessionStorage.setItem('finishLesson', 'false');
    void this.router.navigate(['/lessons-learning/Cross-cutting'], {
      queryParams: { mode: 'catalogue' },
    });
  }

  openLessonForm(): void {
    this.sourcesDrawer.close();
    this.isLessonForm.set(true);
    sessionStorage.removeItem('selectedSet');
    sessionStorage.setItem('stflow', 'false');
    sessionStorage.setItem('formflow', 'true');
    sessionStorage.setItem('lessonFlow', 'false');
    sessionStorage.setItem('finishLesson', 'false');
    void this.router.navigate(['/lessons-learning/Cross-cutting'], {
      queryParams: { mode: 'create' },
    });
  }

  private leaveLessonIfNeeded(): void {
    if (/^\/(lessons-learning|lessons-view)(\/|$)/.test(this.url())) {
      this.eventBus.emitClearLessonsStorage();
    }
  }
}
